import { useState, useEffect } from 'react';
import { ChevronLeft, CreditCard, Smartphone, DollarSign, CheckCircle2, ShieldCheck, Loader2 } from 'lucide-react';
import { useBookingStore } from '../../store/bookingStore';
import { useAuthStore } from '../../store/authStore';
import { useIdentityVerification } from '../../hooks/useAPI';
import supabase from '../../lib/supabase';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import { projectId, publicAnonKey, supabaseUrl } from '../../../utils/supabase/info';

interface CheckoutScreenProps {
  onNavigate: (screen: any) => void;
  onConfirm: () => void;
}

interface StripePaymentFormWrapperProps {
  onSuccess: (paymentIntentId: string) => void;
  onError: (error: string) => void;
  amount: number;
  acceptedTerms: boolean;
  setLoading: (loading: boolean) => void;
}

function StripePaymentFormWrapper({ onSuccess, onError, amount, acceptedTerms, setLoading }: StripePaymentFormWrapperProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      onError('Please accept the terms and conditions');
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message || 'Payment failed');
        setIsProcessing(false);
        setLoading(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      } else {
        onError('Payment was not successful');
        setIsProcessing(false);
        setLoading(false);
      }
    } catch (err: any) {
      onError(err.message || 'An error occurred during payment');
      setIsProcessing(false);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || !acceptedTerms || isProcessing}
        className="w-full h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing Payment...
          </>
        ) : (
          `Pay AED ${amount.toFixed(2)}`
        )}
      </button>
    </form>
  );
}

export default function CheckoutScreen({ onNavigate, onConfirm }: CheckoutScreenProps) {
  const { bookingData, resetBooking, setWaiveDeposit } = useBookingStore();
  const { user } = useAuthStore();
  const { isVerified, isLoading: verifyLoading, startVerification } = useIdentityVerification();
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyPending, setVerifyPending] = useState(false);

  // If the user is already verified, make sure the booking store reflects
  // the waiver so the summary and server price stay consistent.
  useEffect(() => {
    if (isVerified && !bookingData.waiveDeposit) {
      setWaiveDeposit(true);
    }
  }, [isVerified, bookingData.waiveDeposit, setWaiveDeposit]);

  const handleVerifyForWaiver = async () => {
    setVerifyError(null);
    setVerifyPending(false);
    const result = await startVerification();
    if (result.verified) {
      setWaiveDeposit(true);
    } else if (result.pending) {
      setVerifyPending(true);
    } else if (result.error) {
      setVerifyError(result.error.message || 'Verification failed');
    }
  };

  const [paymentMethod, setPaymentMethod] = useState<'card' | 'apple_pay' | 'cash'>('card');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookingReference, setBookingReference] = useState('');

  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCVV, setCardCVV] = useState('');

  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [stripeLoading, setStripeLoading] = useState(false);

  // Create payment intent when card payment is selected
  useEffect(() => {
    if (paymentMethod === 'card' && bookingData.total) {
      createPaymentIntent();
    }
  }, [paymentMethod, bookingData.total]);

  const createPaymentIntent = async () => {
    setStripeLoading(true);
    setError('');

    try {
      const { data: authData } = await supabase.auth.getSession();
      const totalAmount = bookingData.total + bookingData.depositAmount;

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-b295322e/create-payment-intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          body: JSON.stringify({
            amount: totalAmount,
            currency: 'aed',
            metadata: {
              bookingReference: `TEMP-${Date.now()}`,
              userId: authData.session?.user?.id || 'guest',
            },
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create payment intent');
      }

      setClientSecret(result.clientSecret);
      setPaymentIntentId(result.paymentIntentId);
    } catch (err: any) {
      console.error('Payment intent error:', err);
      setError(err.message || 'Failed to initialize payment');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripePaymentSuccess = async (stripePaymentIntentId: string) => {
    // Payment succeeded, now create the booking
    await completeBooking(stripePaymentIntentId);
  };

  const handleStripePaymentError = (errorMessage: string) => {
    setError(errorMessage);
    setLoading(false);
  };

  const handleConfirmBooking = async () => {
    if (!acceptedTerms) {
      setError('Please accept the terms and conditions');
      return;
    }

    if (!bookingData.machine || !bookingData.package) {
      setError('Booking data is incomplete');
      return;
    }

    // For card payments, the Stripe form handles submission
    if (paymentMethod === 'card') {
      setError('Please complete the payment form above');
      return;
    }

    // For non-card payments, proceed directly
    await completeBooking(null);
  };

  const completeBooking = async (stripePaymentIntentId: string | null) => {
    setLoading(true);
    setError('');

    try {
      // Call create-booking Edge Function
      const { data: authData } = await supabase.auth.getSession();

      if (!authData.session) {
        setError('Please log in to complete booking');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-booking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.session.access_token}`,
          },
          body: JSON.stringify({
            machineId: bookingData.machine.id,
            packageId: bookingData.package.id,
            startDate: bookingData.startDate,
            startTime: bookingData.startTime,
            deliveryType: bookingData.deliveryType,
            deliveryAddressId: bookingData.deliveryAddressId,
            pickupLocationId: bookingData.pickupLocationId,
            addOns: bookingData.addOns.map(a => ({
              addOnId: a.addOn.id,
              quantity: a.quantity,
            })),
            specialInstructions: null,
            promoCode: bookingData.promoCode,
            paymentMethod: paymentMethod,
            stripePaymentIntentId: stripePaymentIntentId,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create booking');
      }

      setBookingReference(result.booking.bookingReference);
      setShowSuccess(true);

      // Reset booking after 2 seconds
      setTimeout(() => {
        resetBooking();
        onConfirm();
      }, 2500);
    } catch (err: any) {
      console.error('Booking error:', err);
      setError(err.message || 'Failed to create booking. Please try again.');
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-[#D4F04A] mx-auto mb-6 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-[#1A6B4F]" />
          </div>
          <h2 className="text-2xl mb-2">Booking Confirmed!</h2>
          <p className="text-muted-foreground mb-6">
            Your rental has been successfully booked
          </p>
          <div className="bg-card border border-border rounded-2xl p-6 text-left">
            <div className="text-sm text-muted-foreground mb-2">Booking Reference</div>
            <div className="text-2xl text-[#1A6B4F] mb-4">{bookingReference}</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date & Time</span>
                <span>{bookingData?.startDate} at {bookingData?.startTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Location</span>
                <span>{bookingData?.deliveryAddress || 'Pickup'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span>{bookingData?.package?.name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingData.machine || !bookingData.package) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-muted-foreground">No booking data found</p>
          <button
            onClick={() => onNavigate('home')}
            className="mt-4 h-12 px-6 rounded-xl bg-[#1A6B4F] text-white"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-32">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('booking')}
            className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Checkout</h1>
            <p className="text-xs text-muted-foreground">Review & confirm booking</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-6 text-white">
          <div className="flex items-start gap-3 mb-4">
            <div className="text-4xl">🎾</div>
            <div className="flex-1">
              <h3 className="text-lg mb-1">{bookingData.machine.name}</h3>
              <p className="text-sm opacity-80">{bookingData.machine.type}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="opacity-80">Date</span>
              <span>{bookingData.startDate}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-80">Time</span>
              <span>{bookingData.startTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-80">Duration</span>
              <span>{bookingData.package.name} ({bookingData.package.duration_hours}h)</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-80">Delivery</span>
              <span className="capitalize">{bookingData.deliveryType}</span>
            </div>
            {bookingData.deliveryAddress && (
              <div className="flex justify-between">
                <span className="opacity-80">Location</span>
                <span>{bookingData.deliveryAddress}</span>
              </div>
            )}
            {bookingData.addOns.length > 0 && (
              <div className="flex justify-between">
                <span className="opacity-80">Add-ons</span>
                <span>{bookingData.addOns.length} item{bookingData.addOns.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-base mb-3">Payment Method</h3>
          <div className="space-y-3">
            <button
              onClick={() => setPaymentMethod('card')}
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                paymentMethod === 'card'
                  ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                  : 'border-border bg-card hover:border-[#1A6B4F]/30'
              }`}
            >
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm">Credit / Debit Card</div>
                <div className="text-xs text-muted-foreground">Visa, Mastercard, Amex</div>
              </div>
            </button>

            <button
              onClick={() => setPaymentMethod('apple_pay')}
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                paymentMethod === 'apple_pay'
                  ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                  : 'border-border bg-card hover:border-[#1A6B4F]/30'
              }`}
            >
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm">Apple Pay</div>
                <div className="text-xs text-muted-foreground">One-tap payment</div>
              </div>
            </button>

            <button
              onClick={() => setPaymentMethod('cash')}
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                paymentMethod === 'cash'
                  ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                  : 'border-border bg-card hover:border-[#1A6B4F]/30'
              }`}
            >
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm">Cash on Delivery</div>
                <div className="text-xs text-muted-foreground">Pay when machine arrives</div>
              </div>
            </button>
          </div>
        </div>

        {paymentMethod === 'card' && (
          <div>
            <h3 className="text-base mb-3">Card Details</h3>
            {stripeLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#1A6B4F]" />
                <span className="ml-2 text-sm text-muted-foreground">Loading payment form...</span>
              </div>
            ) : clientSecret ? (
              <Elements stripe={getStripe()} options={{ clientSecret }}>
                <StripePaymentFormWrapper
                  onSuccess={handleStripePaymentSuccess}
                  onError={handleStripePaymentError}
                  amount={bookingData.total + bookingData.depositAmount}
                  acceptedTerms={acceptedTerms}
                  setLoading={setLoading}
                />
              </Elements>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
                {error}
              </div>
            ) : null}
          </div>
        )}

        {!isVerified && bookingData.depositAmount > 0 && (
          <div className="bg-[#D4F04A]/10 border border-[#1A6B4F]/20 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1A6B4F] flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-[#1A6B4F]">
                  Skip the AED {bookingData.depositAmount} deposit
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Verify your ID once with Stripe and the security deposit is
                  waived on this and every future booking.
                </div>
                {verifyPending && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Verification submitted — refreshing shortly.
                  </div>
                )}
                {verifyError && (
                  <div className="text-xs text-destructive mt-2">{verifyError}</div>
                )}
                <button
                  type="button"
                  onClick={handleVerifyForWaiver}
                  disabled={verifyLoading}
                  className="mt-3 h-10 px-4 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm disabled:opacity-60 flex items-center gap-2"
                >
                  {verifyLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting…
                    </>
                  ) : (
                    'Verify ID to waive deposit'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {isVerified && (
          <div className="bg-[#D4F04A]/10 border border-[#1A6B4F]/20 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1A6B4F] flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div className="text-sm text-[#1A6B4F]">
              Identity verified — AED 500 deposit waived.
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-2xl p-6">
          <h4 className="text-base mb-4">Payment Summary</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rental ({bookingData.package.name})</span>
              <span>AED {bookingData.subtotal}</span>
            </div>
            {bookingData.addOnsTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Add-ons</span>
                <span>AED {bookingData.addOnsTotal}</span>
              </div>
            )}
            {bookingData.deliveryFee > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery Fee</span>
                <span>AED {bookingData.deliveryFee}</span>
              </div>
            )}
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Security Deposit</span>
                {bookingData.depositAmount === 0 && bookingData.package?.deposit_required && (
                  <span className="px-2 py-0.5 bg-[#D4F04A]/20 text-[#1A6B4F] text-xs rounded-full">
                    Waived
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {bookingData.depositAmount === 0 && bookingData.package?.deposit_required ? (
                  <>
                    <span className="text-muted-foreground line-through text-sm">
                      AED {bookingData.package.deposit_required}
                    </span>
                    <span className="text-[#1A6B4F]">AED 0</span>
                  </>
                ) : (
                  <span>AED {bookingData.depositAmount}</span>
                )}
              </div>
            </div>
            <div className="h-px bg-border" />
            <div className="flex justify-between text-base">
              <span>Total Amount</span>
              <span className="text-[#1A6B4F] text-lg">
                AED {bookingData.total + bookingData.depositAmount}
              </span>
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground pt-2">
              <ShieldCheck className="w-4 h-4 text-[#1A6B4F] flex-shrink-0 mt-0.5" />
              {bookingData.depositAmount === 0 && bookingData.package?.deposit_required ? (
                <span>
                  {isVerified
                    ? 'Security deposit waived — identity verified'
                    : 'Security deposit waived with Machine Operator add-on'}
                </span>
              ) : (
                <span>Security deposit of AED {bookingData.depositAmount} will be refunded after return</span>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-border accent-[#1A6B4F]"
            />
            <span className="text-sm text-muted-foreground">
              I agree to the rental terms, damage waiver policy, and privacy policy. I understand that the security deposit will be held and refunded upon return of the equipment in good condition.
            </span>
          </label>
        </div>
      </div>

      {paymentMethod !== 'card' && (
        <div className="fixed bottom-20 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
          <button
            onClick={handleConfirmBooking}
            disabled={!acceptedTerms || loading}
            className="w-full h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm Booking'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
